import React, { useEffect, useState } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import {
  VegaBox,
  VegaButton,
  VegaButtonCircle,
  VegaDropdown,
  VegaFlex,
  VegaFont,
  VegaLeftNav,
  VegaLeftNavLink,
  VegaSkeleton,
} from '@globalpayments/vega-react';
import { useLocation, useNavigate } from 'react-router';

import EnvironmentLabel from '../components/environmentLabel';
import { useColorTheme } from '../hooks/useColorTheme';
import { BackgroundImagePreferenceKey } from '../types/localStorage';
import { Pages } from '../types/pages';
import { UserRole } from '../types/users';
import { FeatureFlags } from '../types/featureFlags';
import { useAuth } from '../utility/auth';
import styles from './index.module.css';

interface Props {
  children: React.ReactNode;
}

interface NavigationItem {
  icon: string;
  label: string;
  path: Pages;
  relatedPaths?: Pages[];
  requiredRole?: UserRole;
  featureFlag?: FeatureFlags;
}

const navigationItems: NavigationItem[] = [
  { icon: 'fa-solid fa-house', label: 'Home', path: Pages.Home, relatedPaths: [Pages.Status] },
  {
    icon: 'fa-solid fa-magnifying-glass',
    label: 'Application Lookup',
    path: Pages.UserLookup,
    relatedPaths: [Pages.SessionInfo],
  },
  { icon: 'fa-solid fa-users', label: 'User Client Lookup', path: Pages.UserClientLookup },
  { icon: 'fa-solid fa-filter', label: 'Filtering', path: Pages.Filtering },
  {
    icon: 'fa-solid fa-rectangle-list',
    label: 'Applications List',
    path: Pages.ApplicationsList,
    featureFlag: FeatureFlags.ApplicationsList,
  },
  { icon: 'fa-solid fa-sun', label: 'Opt-Out Tracker', path: Pages.HorizonOptOutTracker },
  { icon: 'fa-solid fa-window-restore', label: 'OD iFrame Test', path: Pages.IFrameTesting },
  {
    icon: 'fa-solid fa-user-shield',
    label: 'User Management',
    path: Pages.UserManagement,
    requiredRole: UserRole.Admin,
  },
];

const pageTitles: Record<string, string> = {
  [Pages.Start.toLowerCase()]: 'Enter Login Code',
  [Pages.Login.toLowerCase()]: 'Login',
  [Pages.Home.toLowerCase()]: 'Home',
  [Pages.Status.toLowerCase()]: 'Home',
  [Pages.UserLookup.toLowerCase()]: 'Application Lookup',
  [Pages.SessionInfo.toLowerCase()]: 'Application Details',
  [Pages.ErrorPage.toLowerCase()]: 'Error',
  [Pages.ConfigSearch.toLowerCase()]: 'Config Lookup',
  [Pages.ConfigInfo.toLowerCase()]: 'Config Details',
  [Pages.UserClientLookup.toLowerCase()]: 'User Client Lookup',
  [Pages.Filtering.toLowerCase()]: 'Filtering',
  [Pages.HorizonOptOutTracker.toLowerCase()]: 'Opt-Out Tracker',
  [Pages.RecentSessions.toLowerCase()]: 'Recent Sessions',
  [Pages.IFrameTesting.toLowerCase()]: 'OD iFrame Test',
  [Pages.UserManagement.toLowerCase()]: 'User Management',
  [Pages.ApplicationsList.toLowerCase()]: 'Applications List',
};

const permanentNavigationQuery = '(min-width: 1640px)';
const isPermanentNavigationViewport = () =>
  typeof window.matchMedia === 'function' && window.matchMedia(permanentNavigationQuery).matches;

type BackgroundImage = 'none' | 'teamCrest' | 'hephaestus' | 'prometheus' | 'powerTheFuture';

const backgroundImages: { key: BackgroundImage; label: string }[] = [
  { key: 'none', label: 'None' },
  { key: 'teamCrest', label: 'Async' },
  { key: 'hephaestus', label: 'Hephaestus' },
  { key: 'prometheus', label: 'Prometheus' },
  { key: 'powerTheFuture', label: 'Global Payments' },
];

const isBackgroundImage = (value: string | null): value is BackgroundImage =>
  backgroundImages.some((backgroundImage) => backgroundImage.key === value);

const getInitialBackgroundImage = (): BackgroundImage => {
  const storedBackgroundImage = localStorage.getItem(BackgroundImagePreferenceKey);
  return isBackgroundImage(storedBackgroundImage) ? storedBackgroundImage : 'none';
};

const Layout: React.FC<Props> = ({ children }) => {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { colorTheme, toggleColorTheme } = useColorTheme();
  const flags = useFlags<Record<string, boolean>>();
  const [backgroundImage, setBackgroundImage] = useState<BackgroundImage>(getInitialBackgroundImage);
  const [isPermanentNavigation, setIsPermanentNavigation] = useState(isPermanentNavigationViewport);
  const [isNavigationOpen, setIsNavigationOpen] = useState(isPermanentNavigationViewport);
  const activeBackgroundImage = auth.user ? backgroundImage : 'none';

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;

    const mediaQuery = window.matchMedia(permanentNavigationQuery);
    const handleBreakpointChange = (event: MediaQueryListEvent) => {
      setIsPermanentNavigation(event.matches);
      setIsNavigationOpen(event.matches);
    };

    mediaQuery.addEventListener('change', handleBreakpointChange);
    return () => mediaQuery.removeEventListener('change', handleBreakpointChange);
  }, []);

  const handleNavigation = (event: CustomEvent, path: Pages) => {
    event.preventDefault();
    navigate(path);
  };

  const handleNavigationStateUpdate = (event: CustomEvent<string>) => {
    if (!isPermanentNavigation) {
      setIsNavigationOpen(event.detail === 'open');
    }
  };

  const isSelected = ({ path, relatedPaths = [] }: NavigationItem) => {
    const currentPath = location.pathname.toLowerCase();
    return [path, ...relatedPaths].some((route) => route.toLowerCase() === currentPath);
  };

  const pageTitle = pageTitles[location.pathname.toLowerCase()] ?? 'MerchantExp Admin';

  const handleSignOut = () => auth.logout(() => navigate(Pages.Home));

  const handleBackgroundImageChange = (event: CustomEvent<string>) => {
    if (!isBackgroundImage(event.detail)) return;

    setBackgroundImage(event.detail);
    localStorage.setItem(BackgroundImagePreferenceKey, event.detail);
  };

  const utilityHeader = (
    <VegaBox padding={{ x: 'size-16', y: 'size-12' }}>
      <VegaFlex alignItems="center" className={styles.headerRow} gap="size-8" justifyContent="space-between">
        <VegaFlex alignItems="center" className={styles.headerIdentity} gap="size-8">
          {auth.user && !isPermanentNavigation && (
            <VegaButtonCircle
              icon="fa-solid fa-bars"
              label="Toggle navigation"
              onVegaClick={() => setIsNavigationOpen((isOpen) => !isOpen)}
              size="small"
              variant="icon-only"
            />
          )}
          <VegaBox className={styles.environmentLabel}>
            <EnvironmentLabel environmentName={import.meta.env.VITE_ENV || 'LOCAL'} />
          </VegaBox>
          <VegaFont as="h1" className={styles.pageTitle} variant={{ default: 'font-h6', M: 'font-h5' }}>
            {pageTitle}
          </VegaFont>
        </VegaFlex>
        <VegaFlex alignItems="center" className={styles.headerActions} gap="size-8">
          {auth.user && (
            <VegaDropdown
              alignment="end"
              isScreenPosition
              onVegaDropdownClick={handleBackgroundImageChange}
              selectedSourceKey={backgroundImage}
              selectType="single"
              source={backgroundImages}
              trigger="click">
              <VegaButtonCircle
                icon="fa-solid fa-image"
                label="Change background image"
                showTooltip
                size="small"
                variant="icon-only"
              />
            </VegaDropdown>
          )}
          <VegaButtonCircle
            icon={colorTheme === 'dark' ? 'fa-solid fa-sun' : 'fa-solid fa-moon'}
            label={`Switch to ${colorTheme === 'dark' ? 'light' : 'dark'} mode`}
            onVegaClick={toggleColorTheme}
            showTooltip
            size="small"
            variant="icon-only"
          />
          {auth.user && (
            <>
              <VegaButtonCircle
                className={styles.mobileSignOut}
                icon="fa-solid fa-right-from-bracket"
                label="Sign out"
                onVegaClick={handleSignOut}
                showTooltip
                size="small"
                variant="icon-only"
              />
              <VegaButton
                className={styles.desktopSignOut}
                icon="fa-solid fa-right-from-bracket"
                iconAlign="left"
                label="Sign out"
                onVegaClick={handleSignOut}
                size="small"
                variant="secondary"
              />
            </>
          )}
        </VegaFlex>
      </VegaFlex>
    </VegaBox>
  );

  return (
    <div className={styles.appShell}>
      {auth.user && (
        <VegaLeftNav
          aria-label="Primary"
          autoCollapseOnOverlay
          footnote={auth.user}
          headerConfig={{ title: 'MerchantExp Admin' }}
          onVegaStateUpdate={handleNavigationStateUpdate}
          open={isNavigationOpen}
          role="navigation"
          showAsOverlay={false}>
          {navigationItems
            .filter(
              (item) =>
                (!item.requiredRole || (!auth.isLoadingRoles && auth.hasRole(item.requiredRole))) &&
                (!item.featureFlag || flags[item.featureFlag] === true),
            )
            .map((item) => (
              <VegaLeftNavLink
                customSelectionEnabled
                icon={item.icon}
                key={item.path}
                onClick={(event) => event.preventDefault()}
                onVegaMenuClick={(event) => handleNavigation(event, item.path)}
                selected={isSelected(item)}
                url={item.path}>
                {item.label}
              </VegaLeftNavLink>
            ))}
          {auth.isLoadingRoles && (
            <VegaBox aria-label="Loading navigation" padding={{ x: 'size-16', y: 'size-12' }} role="status">
              <VegaSkeleton animated type="text" width="70%" />
            </VegaBox>
          )}
        </VegaLeftNav>
      )}
      <VegaFlex
        className={`vega-left-nav-content ${styles.contentColumn} ${styles[`background${activeBackgroundImage}`]}`}
        direction="col"
        gap="0">
        <header className={styles.utilityHeader}>{utilityHeader}</header>
        <main className={styles.mainContent}>
          <VegaBox
            className={styles.pageContent}
            padding={{ default: 'size-24', M: 'size-32', XL: 'size-40' }}
            width="100%">
            {children}
          </VegaBox>
        </main>
      </VegaFlex>
    </div>
  );
};

export default Layout;
